import { BorderedContent } from "@Light/components/page/BorderedContent";
import { Button } from "@Light/components/page/Button";
import { ButtonFooter } from "@Light/components/page/ButtonFooter";
import { useIsSubtleBrand, useScaffold } from "@Light/scaffold";
import { useLight } from "@Light/services/light";
import { useNavigate, useSearchParams } from "@Light/utils/context";
import {
  CheckIcon,
  Cog6ToothIcon,
  GlobeAltIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import React from "react";

type InfoSectionProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
};

const InfoSection = ({ icon, title, description }: InfoSectionProps) => {
  const scaffold = useScaffold();
  return (
    <div className="flex flex-row gap-4">
      <div className="shrink-0">
        <div
          className={clsx(
            "w-8 h-8 rounded-full flex items-center justify-center p-2",
            "bg-gray-100",
          )}
        >
          {icon}
        </div>
      </div>
      <div className="flex flex-col">
        <div
          className={clsx(
            "text-[13px] font-medium leading-5",
            scaffold.page.colors.text.secondary,
          )}
        >
          {title}
        </div>
        <div
          className={clsx(
            "text-[13px] font-light leading-5",
            scaffold.page.colors.text.secondary,
          )}
        >
          {description}
        </div>
      </div>
    </div>
  );
};

export type BatteryIntroProps = {
  onComplete?: (reason: string) => void;
};

export function BatteryIntro({}: BatteryIntroProps) {
  const scaffold = useScaffold();
  const PageBody = scaffold.page.pageBody;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const batteryConnected = searchParams.get("battery_connected");
  const migrateBatteries = searchParams.get("migrate_batteries");

  const isSubtle = useIsSubtleBrand();

  const { useListDevicesQuery } = useLight();
  const { data: devicesResponse } = useListDevicesQuery({
    device_type: "BATTERY",
  });

  // If returning from OAuth, redirect to conclusion where device discovery happens
  React.useEffect(() => {
    if (batteryConnected === "true") {
      navigate(`/devices/link-battery/conclusion?battery_connected=true`);
    }
  }, [batteryConnected, navigate]);

  const batteryDevices = devicesResponse?.data ?? [];
  const device_uuid = batteryDevices[0]?.uuid;

  const headerText = device_uuid ? "One last step" : "What you need to do";
  const buttonText = device_uuid ? "Continue" : "Connect your battery";

  const handleButtonClick = () => {
    if (device_uuid && !migrateBatteries) {
      navigate(`/devices/link-battery/conclusion?device_uuid=${device_uuid}`);
    } else {
      navigate("/devices/link-battery/manufacturer");
    }
  };

  return (
    <PageBody
      centered
      title={
        isSubtle
          ? "Let's finish connecting your battery"
          : `Let's finish connecting your battery to ${scaffold.page.companyName}`
      }
      footer={
        <ButtonFooter>
          <Button
            size="lg"
            onClick={handleButtonClick}
            className="w-full"
            data-testid="battery-intro-continue"
          >
            {buttonText}
          </Button>
        </ButtonFooter>
      }
    >
      <BorderedContent header={headerText}>
        <div className="flex flex-col gap-3">
          <InfoSection
            icon={
              device_uuid ? (
                <CheckIcon className="w-4 h-4 text-gray-600" />
              ) : (
                <GlobeAltIcon className="w-4 h-4 text-gray-600" />
              )
            }
            title="Ensure your battery is online"
            description="Your battery needs to be active and connected to the internet."
          />

          <InfoSection
            icon={
              device_uuid ? (
                <CheckIcon className="w-4 h-4 text-gray-600" />
              ) : (
                <LinkIcon className="w-4 h-4 text-gray-600" />
              )
            }
            title="Connect your battery"
            description="Connecting your battery lets us communicate with it throughout the plan."
          />

          <InfoSection
            icon={<Cog6ToothIcon className="w-4 h-4 text-gray-600" />}
            title="Adjust your battery settings"
            description="We'll let you know which settings need to be adjusted for us to manage your battery."
          />
        </div>
      </BorderedContent>
    </PageBody>
  );
}
